import { useState, useEffect } from 'react';

function getIsMobileMatch(breakpoint: number): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia(`(max-width: ${breakpoint - 1}px)`).matches;
}

/**
 * Hook to detect if the current viewport is mobile-sized
 * @param breakpoint - Width breakpoint in pixels (default: 768px)
 * @returns boolean indicating if viewport is mobile
 */
export function useIsMobile(breakpoint: number = 768): boolean {
  const [isMobile, setIsMobile] = useState(() => getIsMobileMatch(breakpoint));

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const checkMobile = () => {
      setIsMobile(getIsMobileMatch(breakpoint));
    };

    checkMobile();

    // Listen for resize events
    window.addEventListener('resize', checkMobile);

    // Optional: Use matchMedia for more efficient detection
    const mediaQuery = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    const handleMediaChange = (e: MediaQueryListEvent) => {
      setIsMobile(e.matches);
    };

    // Modern browsers support addEventListener on MediaQueryList
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleMediaChange);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleMediaChange);
    }

    return () => {
      window.removeEventListener('resize', checkMobile);
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleMediaChange);
      } else {
        mediaQuery.removeListener(handleMediaChange);
      }
    };
  }, [breakpoint]);

  return isMobile;
}

